import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { apiClient } from "~/api/ApiClient";
import LaunchChecklist from "~/components/autopto/checklist/LaunchChecklist";
import { isAutoptoTrial } from "~/components/autopto/isAutoptoTrial";
import AutoptoWelcome from "~/components/autopto/welcome/AutoptoWelcome";
import NoData from "~/components/containers/NoData";
import {
  SiteWithStats,
  SitesDashboard,
} from "~/components/sites/SitesDashboard";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider";
import { extractHostname } from "~/helpers/helpers.ts";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";
import { isWhiteLabel } from "~/utils/white-label";

import { PaginationConfig } from "../../components/ui/pagination/Pagination";
import { useDebounce } from "../../hooks/useDebounce";
import styles from "./styles.module.scss";

const INITIAL_KEYWORDS_STATE = {
  total: 0,
  top_10: 0,
  top_10_percent: 0,
  top_25: 0,
  top_25_percent: 0,
  life: 0,
  life_percent: 0,
};

const DashboardPage = () => {
  const { team } = useTeam();
  const pageTitle = (!isWhiteLabel() && team?.name) || PAGE_TITLES.dashboard;
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const [sitesWithStats, setSitesWithStats] = useState<SiteWithStats[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [pagination, setPagination] = useState<PaginationConfig>({
    currentPage: 1,
    itemsPerPage: 10,
    nextPage: null,
    prevPage: null,
    totalPages: 1,
    totalItems: 10,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, [debouncedSearchTerm]);

  const { data, isFetching: sitesIsPending } = useQuery({
    queryKey: [
      "dashboard",
      pagination.itemsPerPage,
      pagination.currentPage,
      team?.id,
      debouncedSearchTerm,
    ],
    queryFn: async () => {
      // send only hostname
      const hostname = extractHostname(debouncedSearchTerm);
      const searchValue = hostname.length > 0 ? hostname : debouncedSearchTerm;

      const data = await apiClient.sites.sitesList(
        {
          "page[size]": pagination.itemsPerPage,
          "page[number]": pagination.currentPage,
          q: {
            // @ts-expect-error BE types - missing input field
            name_cont: searchValue,
          },
        },
        {
          headers: {
            "Authorize-Collection-Actions": "create",
            "Authorize-Member-Actions": "destroy",
          },
        },
      );

      const { headers } = data;
      const sitesResponse = data?.data?.sites || [];
      // @ts-expect-error BE types are not correct
      const authorizations = data?.data?.authorizations || {};

      return {
        sites: sitesResponse,
        authorizations,
        paginationMeta: {
          totalItems: Number(headers.get("x-pager-total-count")),
          nextPage: Number(headers.get("x-pager-next-page")),
          prevPage: Number(headers.get("x-pager-prev-page")),
          totalPages: Number(headers.get("x-pager-total-pages")),
        },
      };
    },
  });

  const sites = useMemo(() => data?.sites || [], [data?.sites]);

  useEffect(() => {
    if (!data?.paginationMeta) return;

    setPagination((prev) => ({ ...prev, ...data.paginationMeta }));
  }, [data?.paginationMeta]);

  useEffect(() => {
    if (!sites.length) {
      setSitesWithStats([]);

      return;
    }

    const controller = new AbortController();

    setSitesWithStats(
      sites.map((site) => ({
        ...site,
        stats: {
          keywords: INITIAL_KEYWORDS_STATE,
          recommendations: {
            total: 0,
            active: 0,
          },
          pages: {
            total: 0,
            optimized: 0,
          },
        },
        isStatsLoading: true,
      })),
    );

    sites.forEach(async (site) => {
      try {
        const siteStats = await apiClient.sites.statsDetail(site?.id, {
          signal: controller.signal,
        });
        setSitesWithStats((prev) =>
          prev.map((s) =>
            s.id === site.id
              ? {
                  ...s,
                  stats: siteStats?.data?.site_stats || [],
                  isStatsLoading: false,
                }
              : s,
          ),
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error(`Error fetching stats for site ${site.id}:`, error);
        setSitesWithStats((prev) =>
          prev.map((s) =>
            s.id === site.id
              ? {
                  ...s,
                  stats: {
                    keywords: INITIAL_KEYWORDS_STATE,
                    recommendations: {
                      total: 0,
                      active: 0,
                    },
                    pages: {
                      total: 0,
                      optimized: 0,
                    },
                  },
                  isStatsLoading: false,
                }
              : s,
          ),
        );
      }
    });

    return () => {
      controller.abort();
    };
  }, [sites]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);
  const hasCreatePermission =
    data?.authorizations?.collection?.includes("create");

  const isSearching = debouncedSearchTerm.trim().length > 0;
  const hasSites = sites.length > 0;
  const noResultsForSearch = !sitesIsPending && isSearching && !hasSites;
  // The total, not the page: an emptied last page isn't "no sites".
  const noSitesInDb =
    !sitesIsPending &&
    !isSearching &&
    !hasSites &&
    data?.paginationMeta.totalItems === 0;

  // An Autopto agency in its trial gets the welcome view and the launch
  // checklist; everyone else who can add a site goes straight to the
  // wizard instead of an empty page.
  const isTrial = isAutoptoTrial(team);
  const shouldOpenWizard =
    noSitesInDb && !isTrial && Boolean(hasCreatePermission);

  useEffect(() => {
    if (!shouldOpenWizard) return;

    localStorage.removeItem("siteId");
    navigate(routes.site.wizard({ tab: "new" }), { replace: true });
  }, [shouldOpenWizard, navigate]);

  const renderNoSites = () => {
    if (isTrial) {
      return <AutoptoWelcome canCreateSite={Boolean(hasCreatePermission)} />;
    }

    if (shouldOpenWizard) return null;

    return (
      <NoData
        title="There is no sites here yet"
        description="Looks like it`s kinda empty here. Go ahead and add your first website!"
        path={routes.site.wizard({ tab: "new" })}
        buttonLabel="Add Site"
        disabled={!hasCreatePermission}
        onClick={() => localStorage.removeItem("siteId")}
      />
    );
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{pageTitle}</h1>
      {noSitesInDb ? (
        renderNoSites()
      ) : (
        <SitesDashboard
          noResultsForSearch={noResultsForSearch}
          searchTerm={searchTerm}
          setSearchTerm={handleSearchChange}
          sites={sitesWithStats}
          sitesIsPenging={sitesIsPending}
          pagination={pagination}
          setPagination={setPagination}
          authorizations={data?.authorizations}
          footer={
            isTrial && (
              <div className={styles.launchChecklist}>
                <LaunchChecklist
                  hasSites
                  canCreateSite={Boolean(hasCreatePermission)}
                  canOpenTour
                />
              </div>
            )
          }
        />
      )}
    </div>
  );
};

export default DashboardPage;
